import React from 'react'
import { Button, Card, Input, Modal, Select, Spin } from 'antd'
import { AppstoreOutlined } from '@ant-design/icons'
import { useSearchParams } from 'react-router-dom'
import HomeBookCard from './HomeBookCard'
import HomeSectionState from './HomeSectionState'
import { useHomeCategoryExhibitionStore } from '../../stores/homeCategoryExhibition'
import { useReducedMotionPreference } from '../../utils/homeMotion'
import type { CategoryExhibitionItem } from '../../types/home'

type CategoryExhibitionSectionProps = {
  onBookClick: (item: CategoryExhibitionItem) => void
  onDiscoverMore: () => void
  disableAutoLoad?: boolean
}

const SKELETON_COUNT = 6
const DEFAULT_CATEGORY = 'smart'
const QUERY_CATEGORY_KEY = 'ce_category'
const QUERY_SEARCH_KEY = 'ce_q'
const QUERY_PAGE_KEY = 'ce_page'
const QUERY_SCROLL_KEY = 'ce_scroll'
const QUERY_ANCHOR_BOOK_KEY = 'ce_anchor'
const QUERY_ANCHOR_INDEX_KEY = 'ce_anchor_i'
const QUERY_RESTORE_TS_KEY = 'ce_rt'
const SCROLL_STORAGE_KEY = 'ebookhub:home:category-exhibition:scroll'
const POSITION_STORAGE_KEY = 'ebookhub:home:category-exhibition:position'
const PENDING_RESTORE_KEY = 'ebookhub:home:category-exhibition:pending-restore'
const POSITION_RESTORE_TTL_MS = 30 * 60 * 1000

/**
 * 首页“分类展览”区域。
 */
const CategoryExhibitionSection: React.FC<CategoryExhibitionSectionProps> = ({
  onBookClick,
  onDiscoverMore,
  disableAutoLoad = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const reducedMotion = useReducedMotionPreference()
  const [categoryModalOpen, setCategoryModalOpen] = React.useState(false)
  const [showDelayedSpinner, setShowDelayedSpinner] = React.useState(false)
  const [useModalSelector, setUseModalSelector] = React.useState(false)
  const initializedRef = React.useRef(false)
  const searchTimerRef = React.useRef<number | null>(null)
  const sectionRef = React.useRef<HTMLDivElement | null>(null)
  const cardsContainerRef = React.useRef<HTMLDivElement | null>(null)
  const scrollWriteTimerRef = React.useRef<number | null>(null)
  const restoredScrollRef = React.useRef(false)
  const shouldRestoreRef = React.useRef(false)

  const {
    selectedCategory,
    selectedLabel,
    searchKeyword,
    status,
    loadingMore,
    pageNum,
    hasMore,
    items,
    categories,
    fallback,
    errorMessage,
    setSelectedCategory,
    setSearchKeyword,
    fetchCategories,
    fetchCategoryExhibition,
  } = useHomeCategoryExhibitionStore()

  const clearPersistedPosition = React.useCallback(() => {
    try {
      sessionStorage.removeItem(SCROLL_STORAGE_KEY)
      sessionStorage.removeItem(POSITION_STORAGE_KEY)
      sessionStorage.removeItem(PENDING_RESTORE_KEY)
    } catch {
      void 0
    }
  }, [])

  const markPendingRestore = React.useCallback(() => {
    try {
      sessionStorage.setItem(PENDING_RESTORE_KEY, '1')
    } catch {
      void 0
    }
  }, [])

  const consumePendingRestore = React.useCallback(() => {
    try {
      const value = sessionStorage.getItem(PENDING_RESTORE_KEY) === '1'
      sessionStorage.removeItem(PENDING_RESTORE_KEY)
      return value
    } catch {
      return false
    }
  }, [])

  const detectNavigationRestoreIntent = React.useCallback(() => {
    try {
      const entries = typeof window !== 'undefined' && typeof window.performance?.getEntriesByType === 'function'
        ? window.performance.getEntriesByType('navigation')
        : []
      const navigationEntry = Array.isArray(entries) && entries.length > 0 ? entries[0] as PerformanceNavigationTiming : null
      const isReload = navigationEntry?.type === 'reload'
      return isReload || consumePendingRestore()
    } catch {
      return consumePendingRestore()
    }
  }, [consumePendingRestore])

  const updateCategoryQuery = React.useCallback((payload?: {
    category?: string
    keyword?: string
    pageNum?: number
    scrollY?: number | null
    anchorBookId?: number | string | null
    anchorIndex?: number | null
    restoreTimestamp?: number | null
  }) => {
    const next = new URLSearchParams(searchParams)
    const category = String(payload?.category ?? selectedCategory ?? DEFAULT_CATEGORY).trim() || DEFAULT_CATEGORY
    const keyword = String(payload?.keyword ?? searchKeyword ?? '').trim()
    const page = Math.max(1, Number(payload?.pageNum ?? pageNum ?? 1))
    const scrollY = payload?.scrollY == null ? null : Math.max(0, Math.round(Number(payload.scrollY || 0)))
    const anchorBookId = payload?.anchorBookId
    const anchorIndex = payload?.anchorIndex
    const restoreTimestamp = payload?.restoreTimestamp == null
      ? null
      : Math.max(0, Math.round(Number(payload.restoreTimestamp || 0)))

    if (category === DEFAULT_CATEGORY) {
      next.delete(QUERY_CATEGORY_KEY)
    } else {
      next.set(QUERY_CATEGORY_KEY, category)
    }

    if (keyword) {
      next.set(QUERY_SEARCH_KEY, keyword)
    } else {
      next.delete(QUERY_SEARCH_KEY)
    }

    if (page > 1) {
      next.set(QUERY_PAGE_KEY, String(page))
    } else {
      next.delete(QUERY_PAGE_KEY)
    }

    if (scrollY != null) {
      next.set(QUERY_SCROLL_KEY, String(scrollY))
    } else if (payload && Object.prototype.hasOwnProperty.call(payload, 'scrollY')) {
      next.delete(QUERY_SCROLL_KEY)
    }

    if (anchorBookId != null && String(anchorBookId).trim()) {
      next.set(QUERY_ANCHOR_BOOK_KEY, String(anchorBookId).trim())
    } else if (payload && Object.prototype.hasOwnProperty.call(payload, 'anchorBookId')) {
      next.delete(QUERY_ANCHOR_BOOK_KEY)
    }

    if (anchorIndex != null && Number.isFinite(Number(anchorIndex))) {
      next.set(QUERY_ANCHOR_INDEX_KEY, String(Math.max(0, Math.trunc(Number(anchorIndex)))))
    } else if (payload && Object.prototype.hasOwnProperty.call(payload, 'anchorIndex')) {
      next.delete(QUERY_ANCHOR_INDEX_KEY)
    }

    if (restoreTimestamp != null && restoreTimestamp > 0) {
      next.set(QUERY_RESTORE_TS_KEY, String(restoreTimestamp))
    } else if (payload && Object.prototype.hasOwnProperty.call(payload, 'restoreTimestamp')) {
      next.delete(QUERY_RESTORE_TS_KEY)
    }

    setSearchParams(next, { replace: true })
  }, [pageNum, searchKeyword, searchParams, selectedCategory, setSearchParams])

  React.useEffect(() => {
    const checkUseModal = () => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        setUseModalSelector(false)
        return
      }
      try {
        const isCoarse = window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(hover: none)').matches
        setUseModalSelector(Boolean(isCoarse))
      } catch {
        setUseModalSelector(false)
      }
    }
    checkUseModal()
    window.addEventListener('resize', checkUseModal)
    return () => window.removeEventListener('resize', checkUseModal)
  }, [])

  React.useEffect(() => {
    if (disableAutoLoad) return
    if (initializedRef.current) return
    initializedRef.current = true
    shouldRestoreRef.current = detectNavigationRestoreIntent()
    const initialCategory = shouldRestoreRef.current
      ? String(searchParams.get(QUERY_CATEGORY_KEY) || selectedCategory || DEFAULT_CATEGORY).trim() || DEFAULT_CATEGORY
      : (String(selectedCategory || DEFAULT_CATEGORY).trim() || DEFAULT_CATEGORY)
    const initialKeyword = shouldRestoreRef.current
      ? String(searchParams.get(QUERY_SEARCH_KEY) || '').trim()
      : ''
    const initialPageNum = shouldRestoreRef.current
      ? Math.max(1, Number(searchParams.get(QUERY_PAGE_KEY) || 1))
      : 1
    restoredScrollRef.current = false
    setSelectedCategory(initialCategory)
    setSearchKeyword(initialKeyword)
    void (async () => {
      await fetchCategories(initialKeyword)
      await fetchCategoryExhibition(initialCategory, { force: true, pageNum: 1 })
      if (initialPageNum > 1) {
        for (let currentPageNum = 2; currentPageNum <= initialPageNum; currentPageNum += 1) {
          // 逐页追加，确保刷新后能恢复到更接近原来看到的卡片行位置。
          await fetchCategoryExhibition(initialCategory, { append: true, force: true, pageNum: currentPageNum })
        }
      }
    })()
  }, [detectNavigationRestoreIntent, disableAutoLoad, fetchCategories, fetchCategoryExhibition, searchParams, selectedCategory, setSearchKeyword, setSelectedCategory])

  React.useEffect(() => {
    if (disableAutoLoad) return
    if (restoredScrollRef.current) return
    if (status === 'loading') return
    const section = sectionRef.current
    if (!section) return
    const requestedPageNum = Math.max(1, Number(searchParams.get(QUERY_PAGE_KEY) || 1))
    if (!shouldRestoreRef.current) {
      restoredScrollRef.current = true
      return
    }
    const queryRestoreTimestamp = Math.max(0, Number(searchParams.get(QUERY_RESTORE_TS_KEY) || 0))
    const queryAnchorBookId = String(searchParams.get(QUERY_ANCHOR_BOOK_KEY) || '').trim()
    const queryAnchorIndex = Math.max(0, Number(searchParams.get(QUERY_ANCHOR_INDEX_KEY) || 0))
    const storedPosition = (() => {
      try {
        const raw = sessionStorage.getItem(POSITION_STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
      } catch {
        return null
      }
    })()
    const restoreTimestamp = Math.max(0, Number(storedPosition?.timestamp || queryRestoreTimestamp || 0))
    const isRestoreExpired = !(restoreTimestamp > 0) || (Date.now() - restoreTimestamp) > POSITION_RESTORE_TTL_MS
    if (isRestoreExpired) {
      clearPersistedPosition()
      updateCategoryQuery({
        scrollY: null,
        anchorBookId: null,
        anchorIndex: null,
        restoreTimestamp: null,
      })
      restoredScrollRef.current = true
      return
    }
    const anchorBookId = queryAnchorBookId || String(storedPosition?.anchorBookId || '').trim()
    const anchorIndex = anchorBookId ? Number(storedPosition?.anchorIndex ?? queryAnchorIndex ?? 0) : queryAnchorIndex
    if ((anchorBookId || Number.isFinite(anchorIndex)) && cardsContainerRef.current) {
      const anchorElement = anchorBookId
        ? cardsContainerRef.current.querySelector<HTMLElement>(`[data-book-id="${anchorBookId}"]`)
        : null
      const indexElement = !anchorElement && Number.isFinite(anchorIndex)
        ? cardsContainerRef.current.querySelector<HTMLElement>(`[data-card-index="${Math.max(0, Math.trunc(anchorIndex))}"]`)
        : null
      const targetElement = anchorElement || indexElement
      if (targetElement) {
        const timer = window.setTimeout(() => {
          targetElement.scrollIntoView({
            behavior: 'auto',
            block: 'nearest',
            inline: 'nearest',
          })
          restoredScrollRef.current = true
        }, 0)
        return () => window.clearTimeout(timer)
      }
      const stillLoadingAnchorPage = hasMore || pageNum < requestedPageNum
      if (stillLoadingAnchorPage) {
        return
      }
    }
    const queryScroll = Number(searchParams.get(QUERY_SCROLL_KEY) || 0)
    const storedScroll = (() => {
      try {
        return Number(sessionStorage.getItem(SCROLL_STORAGE_KEY) || 0)
      } catch {
        return 0
      }
    })()
    const targetScroll = Math.max(0, queryScroll || storedScroll)
    if (!(targetScroll > 0)) {
      restoredScrollRef.current = true
      return
    }
    const timer = window.setTimeout(() => {
      window.scrollTo({
        top: targetScroll,
        behavior: reducedMotion ? 'auto' : 'auto',
      })
      restoredScrollRef.current = true
    }, 0)
    return () => window.clearTimeout(timer)
  }, [clearPersistedPosition, disableAutoLoad, hasMore, items.length, pageNum, reducedMotion, searchParams, status, updateCategoryQuery])

  React.useEffect(() => {
    if (disableAutoLoad) return
    const handleScroll = () => {
      const section = sectionRef.current
      if (!section) return
      const rect = section.getBoundingClientRect()
      const viewportHeight = window.innerHeight || 0
      const isNearViewport = rect.top < viewportHeight && rect.bottom > 0
      if (!isNearViewport) return
      if (scrollWriteTimerRef.current) {
        window.clearTimeout(scrollWriteTimerRef.current)
      }
      scrollWriteTimerRef.current = window.setTimeout(() => {
        const currentScrollY = Math.max(0, Math.round(window.scrollY || window.pageYOffset || 0))
        const restoreTimestamp = Date.now()
        const cards = [...(cardsContainerRef.current?.querySelectorAll<HTMLElement>('[data-category-card="1"]') || [])]
        const visibleCards = cards.filter((card) => {
          const cardRect = card.getBoundingClientRect()
          return cardRect.top < viewportHeight && cardRect.bottom > 0
        })
        const anchorCard = (visibleCards.length > 0 ? visibleCards : cards)
          .map((card) => ({
            element: card,
            distance: Math.abs(card.getBoundingClientRect().top - Math.max(0, rect.top)),
          }))
          .sort((left, right) => left.distance - right.distance)[0]?.element || null
        const anchorBookId = String(anchorCard?.dataset.bookId || '').trim()
        const anchorIndex = Math.max(0, Number(anchorCard?.dataset.cardIndex || 0))
        try {
          sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify({ scrollY: currentScrollY, timestamp: restoreTimestamp }))
          sessionStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({
            anchorBookId,
            anchorIndex,
            scrollY: currentScrollY,
            timestamp: restoreTimestamp,
          }))
        } catch {
          void 0
        }
        updateCategoryQuery({
          scrollY: currentScrollY,
          anchorBookId: anchorBookId || null,
          anchorIndex: Number.isFinite(anchorIndex) ? anchorIndex : null,
          restoreTimestamp,
        })
      }, 120)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollWriteTimerRef.current) {
        window.clearTimeout(scrollWriteTimerRef.current)
      }
    }
  }, [disableAutoLoad, updateCategoryQuery])

  React.useEffect(() => {
    if (disableAutoLoad) return
    return () => {
      if (!restoredScrollRef.current && items.length <= 0) return
      markPendingRestore()
    }
  }, [disableAutoLoad, items.length, markPendingRestore])

  React.useEffect(() => {
    if (status !== 'loading') {
      setShowDelayedSpinner(false)
      return
    }
    if (reducedMotion) {
      setShowDelayedSpinner(false)
      return
    }
    const timer = window.setTimeout(() => setShowDelayedSpinner(true), 300)
    return () => window.clearTimeout(timer)
  }, [reducedMotion, status])

  const handleCategoryChange = React.useCallback((nextCategory: string) => {
    const category = String(nextCategory || DEFAULT_CATEGORY).trim() || DEFAULT_CATEGORY
    setSelectedCategory(category)
    setSearchKeyword('')
    clearPersistedPosition()
    updateCategoryQuery({ category, keyword: '', pageNum: 1, scrollY: null, anchorBookId: null, anchorIndex: null, restoreTimestamp: null })
    void fetchCategoryExhibition(category, { force: true, pageNum: 1 })
  }, [clearPersistedPosition, fetchCategoryExhibition, setSearchKeyword, setSelectedCategory, updateCategoryQuery])

  const handleCategorySearch = React.useCallback((keyword: string) => {
    const nextKeyword = String(keyword || '')
    setSearchKeyword(nextKeyword)
    clearPersistedPosition()
    updateCategoryQuery({ keyword: nextKeyword, pageNum: 1, scrollY: null, anchorBookId: null, anchorIndex: null, restoreTimestamp: null })
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current)
    }
    searchTimerRef.current = window.setTimeout(() => {
      void fetchCategories(nextKeyword)
    }, 180)
  }, [clearPersistedPosition, fetchCategories, setSearchKeyword, updateCategoryQuery])

  const handleKeywordSubmit = React.useCallback(() => {
    const trimmedKeyword = String(searchKeyword || '').trim()
    if (!trimmedKeyword) return
    const matched = categories.find((item) => item.value !== 'smart')
    const target = matched?.value || trimmedKeyword
    setSelectedCategory(target)
    clearPersistedPosition()
    updateCategoryQuery({ category: target, keyword: trimmedKeyword, pageNum: 1, scrollY: null, anchorBookId: null, anchorIndex: null, restoreTimestamp: null })
    void fetchCategoryExhibition(target, { force: true, pageNum: 1 })
  }, [categories, clearPersistedPosition, fetchCategoryExhibition, searchKeyword, setSelectedCategory, updateCategoryQuery])

  const handleLoadMore = React.useCallback(() => {
    if (!hasMore || loadingMore || status === 'loading') return
    const nextPageNum = pageNum + 1
    updateCategoryQuery({ pageNum: nextPageNum })
    void fetchCategoryExhibition(selectedCategory, { append: true, pageNum: nextPageNum })
  }, [fetchCategoryExhibition, hasMore, loadingMore, pageNum, selectedCategory, status, updateCategoryQuery])

  React.useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current)
      }
      if (scrollWriteTimerRef.current) {
        window.clearTimeout(scrollWriteTimerRef.current)
      }
    }
  }, [fetchCategoryExhibition, setSelectedCategory])

  const selectorNode = (
    <div className="flex flex-col items-end gap-1">
      {useModalSelector ? (
        <>
          <Button icon={<AppstoreOutlined />} onClick={() => setCategoryModalOpen(true)}>
            {selectedLabel}
          </Button>
          <Modal
            title="选择分类"
            open={categoryModalOpen}
            onCancel={() => setCategoryModalOpen(false)}
            footer={null}
          >
            <Input.Search
              placeholder="输入分类号或分类关键字"
              allowClear
              value={searchKeyword}
              onChange={(event) => handleCategorySearch(event.target.value)}
              onSearch={() => handleKeywordSubmit()}
              className="mb-3"
            />
            <div className="grid grid-cols-2 gap-2">
              {categories.map((item) => (
                <Button
                  key={item.value}
                  type={item.value === selectedCategory ? 'primary' : 'default'}
                  onClick={() => {
                    setCategoryModalOpen(false)
                    handleCategoryChange(item.value)
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </Modal>
        </>
      ) : (
        <Select
          value={selectedCategory}
          options={categories.map((item) => ({ value: item.value, label: item.label }))}
          onChange={handleCategoryChange}
          showSearch
          filterOption={false}
          searchValue={searchKeyword}
          onSearch={handleCategorySearch}
          onInputKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              handleKeywordSubmit()
            }
          }}
          placeholder="输入分类号或分类关键字"
          notFoundContent={searchKeyword ? '未匹配到分类，请继续输入或按回车尝试' : null}
          style={{ minWidth: 180 }}
          size="middle"
          popupMatchSelectWidth={false}
        />
      )}
      <div className="text-[11px] leading-4 text-gray-400">可选分类，也可输分类号/分类关键字</div>
    </div>
  )

  const skeletonCards = (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
      {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
        <div
          key={index}
          className={`rounded-lg border border-gray-100 bg-white overflow-hidden ${reducedMotion ? '' : 'will-change-transform'}`}
          style={reducedMotion ? undefined : { transform: 'translateZ(0)' }}
        >
          <div className={`aspect-[3/4] bg-gray-100 ${reducedMotion ? '' : 'category-exhibition-skeleton'}`} />
          <div className="p-3">
            <div className={`h-4 rounded bg-gray-100 ${reducedMotion ? '' : 'category-exhibition-skeleton'}`} />
            <div className={`mt-2 h-3 rounded bg-gray-100 ${reducedMotion ? '' : 'category-exhibition-skeleton'}`} />
            <div className={`mt-2 h-3 rounded bg-gray-100 w-2/3 ${reducedMotion ? '' : 'category-exhibition-skeleton'}`} />
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <Card
      ref={sectionRef}
      title={<span className="text-lg font-bold text-gray-800">分类展览</span>}
      extra={selectorNode}
      className="theme-card border-none mb-6"
      styles={{ header: { borderBottom: '1px solid var(--color-primary-50)' } }}
    >
      <style>{`
        @keyframes categoryExhibitionPulse {
          0% { opacity: 0.55; transform: translateZ(0) scale(1); }
          50% { opacity: 0.95; transform: translateZ(0) scale(1.01); }
          100% { opacity: 0.55; transform: translateZ(0) scale(1); }
        }
        .category-exhibition-skeleton {
          animation: categoryExhibitionPulse 1.2s ease-in-out infinite;
          will-change: transform, opacity;
        }
      `}</style>
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-gray-500">
          {selectedLabel === '为你推荐' ? '基于你的阅读、收藏与评分偏好生成' : `当前分类：${selectedLabel}`}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-gray-400">第 {pageNum} 页</div>
          {showDelayedSpinner ? <Spin size="small" /> : null}
        </div>
      </div>

      {status === 'loading' ? skeletonCards : null}

      {status === 'error' ? (
        <HomeSectionState
          type="error"
          title="网络异常"
          description={errorMessage || '分类展览加载失败，请稍后重试'}
          actionLabel="重新加载"
          onAction={() => { void fetchCategoryExhibition(selectedCategory, { force: true }) }}
        />
      ) : null}

      {status !== 'loading' && status !== 'error' && items.length === 0 ? (
        <HomeSectionState
          type="empty"
          title={fallback ? '当前分类暂无个性化结果' : '暂无分类书籍'}
          description={fallback ? '已为你准备其它热门内容，欢迎去搜索页发现更多好书' : '该分类下暂时没有可展示的书籍'}
          actionLabel="去发现好书"
          onAction={onDiscoverMore}
        />
      ) : null}

      {status !== 'loading' && status !== 'error' && items.length > 0 ? (
        <div>
          <div ref={cardsContainerRef} className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {items.map((item, index) => (
              <div
                key={`${item.book_id}-${selectedCategory}-${index}`}
                data-category-card="1"
                data-book-id={String(item.book_id)}
                data-card-index={String(index)}
              >
                <HomeBookCard
                  id={item.book_id}
                  title={item.title}
                  author={item.author}
                  cover={item.cover}
                  format={item.format}
                  avgRating={item.avg_rating}
                  ratingCount={item.rating_count}
                  readProgressPercent={item.read_progress_percent}
                  badgeText={selectedCategory === 'smart' ? `推荐 ${index + 1}` : `分类 ${index + 1}`}
                  description={item.recommendation_reason}
                  onClick={() => onBookClick(item)}
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-xs text-gray-400">已展示 {items.length} 本{hasMore ? '，可继续加载更多' : ''}</div>
            {hasMore ? (
              <Button onClick={handleLoadMore} loading={loadingMore}>
                更多
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </Card>
  )
}

export default CategoryExhibitionSection
